import { createSelector } from 'reselect';
import { getAnalyticsCategories } from 'features/analytics/utils/getAnalyticsCategories';
import checkFeatureFlag from 'utils/featureFlags';
import { emptyArray } from '../../constants';
const selectSetUpEmmaMonthlyMerchants = (store) => store.setUpEmma.monthlyMerchants;
export const selectSetUpEmmaMonthlyCategories = (store) => store.setUpEmma.monthlyCategories;
export const selectSetUpEmmaMerchantSections = createSelector([selectSetUpEmmaMonthlyMerchants], (monthlyMerchants) => {
    const filtered = monthlyMerchants?.merchants.filter((merchant) => merchant.transactionsCount > 0) ?? [];
    return filtered.map((data, index) => ({
        type: 'merchant',
        data,
        index,
        isLast: index === filtered.length - 1,
    }));
});
const selectSetUpEmmaCategoriesMap = createSelector([selectSetUpEmmaMonthlyCategories], (data) => {
    if (!data)
        return undefined;
    const sorted = [...data.categories].sort((a, b) => a.total > b.total ? 1 : -1);
    return sorted.reduce((acc, curr) => {
        if (curr.parentCategoryId) {
            acc.subCategories[curr.id] = curr;
        }
        else {
            acc.parentCategories[curr.id] = curr;
        }
        return acc;
    }, {
        parentCategories: {},
        subCategories: {},
    });
});
export const selectSetUpEmmaCategorySections = createSelector([selectSetUpEmmaMonthlyCategories, selectSetUpEmmaCategoriesMap], (data, categoriesMap) => {
    if (!categoriesMap || !data?.categories || !data.transactionsCount) {
        return emptyArray;
    }
    const categories = getAnalyticsCategories(Object.values(categoriesMap.parentCategories));
    const { currency, spending: totalSpending } = data;
    const expenses = categories[0].data;
    const income = categories.length > 1 ? categories[1].data : [];
    const incomeTotal = categories.length > 1 && categories[1] ? categories[1].total : 0;
    const sections = [];
    const visibleExpensesWithTransactions = expenses.filter((e) => e.transactionsCount > 0 && !e.hideFromStats);
    if (visibleExpensesWithTransactions.length > 0) {
        sections.push({
            type: 'header',
            data: {
                id: 'spending',
                title: 'Spending',
                amount: -totalSpending,
                currency,
            },
        });
        sections.push(...visibleExpensesWithTransactions.map((item, index) => ({
            type: 'category',
            data: item,
            totalSpending,
            index,
            isLast: index === visibleExpensesWithTransactions.length - 1,
        })));
    }
    if (income.length > 0) {
        sections.push({
            type: 'header',
            data: { id: 'income', title: 'Income', amount: incomeTotal, currency },
        });
        sections.push(...income.map((item, index) => ({
            type: 'category',
            totalSpending: incomeTotal,
            data: item,
            index,
            isLast: index === income.length - 1,
        })));
    }
    const hiddenExpensesWithTransactions = expenses.filter((e) => e.transactionsCount > 0 && e.hideFromStats);
    if (hiddenExpensesWithTransactions.length > 0) {
        sections.push({
            type: 'header',
            data: {
                id: 'excluded',
                title: 'Excluded categories',
                amount: 0,
                currency,
            },
        });
        sections.push(...hiddenExpensesWithTransactions.map((item, index) => ({
            type: 'category',
            data: item,
            index,
            isLast: index === hiddenExpensesWithTransactions.length - 1,
        })));
    }
    return sections;
});
export const selectShouldShowEmmaSetUpForBasicCustomers = createSelector([(store) => store.utils.featureFlags], (featureFlags) => checkFeatureFlag(featureFlags, 'ab_test_emma_setup_basic_customers'));
export const selectShouldShowEmmaSetUpCustomersWithFreeTrial = createSelector([(store) => store.utils.featureFlags], (featureFlags) => checkFeatureFlag(featureFlags, 'ab_test_emma_setup_free_trial_customers'));
export const selectShouldShowEmmaSetUpFlow = createSelector([
    selectShouldShowEmmaSetUpForBasicCustomers,
    selectShouldShowEmmaSetUpCustomersWithFreeTrial,
    (state) => state.user.user.guessedHomeCountry === 'GB',
    (state) => state.deviceSettings.showSetUpFlow,
], (showForBasicCustomers, showForCustomersWithFreeTrial, isUserGB, showSetUpFlow) => showSetUpFlow &&
    isUserGB &&
    (showForBasicCustomers || showForCustomersWithFreeTrial));
